import React from 'react';

import { classes, st } from '../../CornerRadiusInput.st.css';

import { RoundedCorner } from '@wix/wix-ui-icons-common';
import Input from '../../../Input';
import NumberInput from '../../../NumberInput';
import FormField from '../../../FormField';
import {
  CornerProps,
  CornerRadiusInputProps,
} from '../../CornerRadiusInput.types';
import { dataHooks } from '../../CornerRadiusInput.constants';
import { useIcons } from '../../../WixDesignSystemIconThemeProvider';

const Suffix = ({ children }: { children: React.ReactNode }) => {
  return <Input.Affix>{children}</Input.Affix>;
};

type InputFieldProps = Partial<CornerProps> & {
  position: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
  size: CornerRadiusInputProps['size'];
  isLinkingEnabled: boolean;
  linkedOnInvalid: CornerProps['onInvalid'];
  linkedOnChange: CornerProps['onChange'];
  linkedOnBlur: () => void;
  min: CornerRadiusInputProps['min'];
  max: CornerRadiusInputProps['max'];
};

const InputField = ({
  id,
  name,
  position,
  status,
  suffix,
  size,
  ariaLabel,
  onInvalid,
  onChange,
  value,
  isLinkingEnabled,
  linkedOnInvalid,
  linkedOnChange,
  linkedOnBlur,
  min,
  max,
}: InputFieldProps) => {
  const icons = useIcons('CornerRadiusInputField', {
    RoundedCorner,
  });

  // original icon is top-right
  const cornerIcons = {
    topLeft: (
      <Input.IconAffix>
        <icons.RoundedCorner
          className={st(classes.cornerIcon, { position: 'topLeft' })}
        />
      </Input.IconAffix>
    ),
    topRight: (
      <Input.IconAffix>
        <icons.RoundedCorner className={st(classes.cornerIcon)} />
      </Input.IconAffix>
    ),
    bottomLeft: (
      <Input.IconAffix>
        <icons.RoundedCorner
          className={st(classes.cornerIcon, { position: 'bottomLeft' })}
        />
      </Input.IconAffix>
    ),
    bottomRight: (
      <Input.IconAffix>
        <icons.RoundedCorner
          className={st(classes.cornerIcon, { position: 'bottomRight' })}
        />
      </Input.IconAffix>
    ),
  };

  const dataHook = dataHooks[position];
  const icon = cornerIcons[position];

  return (
    <FormField status={status}>
      <NumberInput
        id={id}
        name={name}
        dataHook={dataHook}
        size={size}
        prefix={icon}
        min={min}
        max={max}
        suffix={<Suffix>{suffix || 'px'}</Suffix>}
        hideStepper
        ariaLabel={ariaLabel}
        value={value}
        onInvalid={isLinkingEnabled ? linkedOnInvalid : onInvalid}
        onChange={isLinkingEnabled ? linkedOnChange : onChange}
        onBlur={() => {
          isLinkingEnabled && linkedOnBlur();
        }}
      />
    </FormField>
  );
};

export { InputField };
